import * as React from 'react';
import * as _ from 'lodash';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Notification,
  Logging,
  renderEdit,
  API,
  TaxPayer,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  Footer,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import TaxPayerItemProps from './TaxPayerProps.interface';
import '../common-settings.scss';
import './taxpayer.scss';
import useTaxPayer from './useTaxPayer';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

interface ParamTypes {
  uuid: string,
}

const TaxPayerItem = ({
  gatewayEndPoint,
}: TaxPayerItemProps): JSX.Element => {
  // Get the Uuid from the Url Parameters
  const { uuid } = useParams<ParamTypes>();
  const [isLoaded, setIsLoaded] = React.useState(uuid === undefined);

  // Load the Taxpayer if the UUID is specified during an Edit
  const [taxPayer, taxPayerNotification] = uuid ? useTaxPayer(gatewayEndPoint, uuid) : [null, null];

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);
  const [redirect, setRedirect] = React.useState(null);

  const [taxpayerName, setTaxpayerName] = React.useState(null);
  const [taxpayerCode, setTaxpayerCode] = React.useState(null);
  const [taxpayerDivision, setTaxpayerDivision] = React.useState(null);
  const [taxpayerDepartment, setTaxpayerDepartment] = React.useState(null);
  const [taxpayerPartition, setTaxpayerPartition] = React.useState(null);

  // Make the API Call to Add the Taxpayer
  const addTaxPayer = (taxpayer: TaxPayer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Tax Payer
      await api.settings.addTaxPayerV41(taxpayer);

      // Direct back to Tax Payers
      setRedirect('/settings/taxpayers');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Taxpayer Save Error',
        description: `Unable to Add the Taxpayer '${taxpayer.taxPayerName}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409, 400],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Edit the Taxpayer
  const editTaxPayer = (taxpayer: TaxPayer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Edit the Tax Payer
      await api.settings.editTaxPayerV41(taxpayer);

      // Direct back to Tax Payers
      setRedirect('/settings/taxpayers');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Taxpayer Save Error',
        description: `Unable to Edit the Taxpayer '${taxpayer.taxPayerName}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409, 400],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    const taxpayer: TaxPayer = {
      uuid,
      taxPayerName: taxpayerName,
      companyCode: taxpayerCode,
      divisionCode: taxpayerDivision,
      departmentCode: taxpayerDepartment,
      partitionName: taxpayerPartition,
    };

    if (uuid) {
      Logging.Info('Edit Tax Payer', taxpayer);

      dispatch(editTaxPayer(taxpayer));
    } else {
      Logging.Info('Add Tax Payer', taxpayer);

      dispatch(addTaxPayer(taxpayer));
    }
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const validateValues = (): boolean => {
    if (!taxpayerName) {
      return false;
    }

    if (!taxpayerCode) {
      return false;
    }

    if (!taxpayerPartition) {
      return false;
    }

    // If any Taxpayer field is specified, the Taxpayer Code and Partition is required
    if (taxpayerCode || taxpayerPartition || taxpayerDepartment || taxpayerDivision) {
      if (!taxpayerCode || !taxpayerPartition) {
        return false;
      }
    }

    // If the Taxpayer Department is specified, the Taxpayer Code, Division and partition is required
    if (taxpayerDepartment) {
      if (!taxpayerCode || !taxpayerPartition || !taxpayerDivision) {
        return false;
      }
    }

    if (taxpayerName?.length > 60) {
      return false;
    }

    if (taxpayerCode?.length > 40) {
      return false;
    }

    if (taxpayerPartition?.length > 30) {
      return false;
    }

    if (taxpayerDepartment?.length > 40) {
      return false;
    }

    if (taxpayerDivision?.length > 40) {
      return false;
    }

    return true;
  };

  // TaxPayer was loaded during an Edit
  React.useEffect(() => {
    if (taxPayer) {
      setTaxpayerName(taxPayer.taxPayerName);
      setTaxpayerCode(taxPayer.companyCode);
      setTaxpayerDivision(taxPayer.divisionCode);
      setTaxpayerDepartment(taxPayer.departmentCode);
      setTaxpayerPartition(taxPayer.partitionName);

      setIsLoaded(true);
    }
  }, [taxPayer]);

  // Unable to Load the TayPayer during an Edit
  React.useEffect(() => {
    if (taxPayerNotification) {
      setNotification(taxPayerNotification);
    }
  }, [taxPayerNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('TaxPayer: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setRedirect(null);
    };
  }, []);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const controlValidation: ControlValidation = {
    requiredFields: ['taxpayerName', 'taxpayerCode', 'taxpayerPartition'],
    minMaxSize: [
      { field: 'taxpayerName', maxSize: 60 },
      { field: 'taxpayerCode', maxSize: 40 },
      { field: 'taxpayerDivision', maxSize: 40 },
      { field: 'taxpayerDepartment', maxSize: 40 },
      { field: 'taxpayerPartition', maxSize: 30 },
    ],
    customMessages: [],
  };

  // If any Taxpayer field is specified, the Taxpayer Code and Partition are required
  if (taxpayerCode || taxpayerPartition || taxpayerDivision || taxpayerDepartment) {
    controlValidation.requiredFields = [...controlValidation.requiredFields, 'taxpayerPartition', 'taxpayerCode'];
  }

  // If the Taxpayer Department is specified, the Taxpayer Code, Partition, Division are required
  if (taxpayerDepartment) {
    controlValidation.requiredFields = [...controlValidation.requiredFields, 'taxpayerPartition', 'taxpayerCode', 'taxpayerDivision'];
  }

  const canSave: boolean = validateValues();

  return (
    <>
      {
        !isLoaded
          ? (
            !taxPayerNotification
            && (
              <ContentLoading loadingMessage="Loading Taxpayer ..." />
            )
          )
          : (
            <div className="settingsContainer">
              <div className="settingsHeaderBack">
                <VertexLink href="#/settings/taxpayers" display="Back to Taxpayers" />
              </div>
              <div className="settingsHeader">
                <div className="settingsHeaderLeft">
                  {uuid ? 'Edit Taxpayer' : 'Add Taxpayer'}
                </div>
              </div>
              <hr />
              <div className="settingsContentControls">
                {renderEdit(33, 'taxpayerName', 'Taxpayer Name*', 'text', 'Taxpayer Name',
                  taxpayerName, setTaxpayerName, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'taxpayerCode', 'Taxpayer Code', 'text', 'Taxpayer Code', taxpayerCode,
                  setTaxpayerCode, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'taxpayerDivision', 'Taxpayer Division', 'text', 'Taxpayer Division', taxpayerDivision,
                  setTaxpayerDivision, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'taxpayerDepartment', 'Taxpayer Department', 'text', 'Taxpayer Department', taxpayerDepartment,
                  setTaxpayerDepartment, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'taxpayerPartition', 'Taxpayer Partition', 'text', 'Taxpayer Partition', taxpayerPartition,
                  setTaxpayerPartition, controlStateEditable, controlStyle, controlValidation)}
              </div>
              <Footer cancelLink="#/settings/taxpayers" saveEnabled={canSave} onSave={onSave} />
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default TaxPayerItem;
